import React, { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, Flex, Heading, Text, Button, Card, CardHeader, CardBody,
  Icon, Alert, AlertIcon, Spinner, Input, InputGroup, InputRightElement, Divider,
  SimpleGrid,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { MdVerifiedUser } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../api/AxiosInstance';

const fmtDate = (d: any) =>
  d ? new Date(d).toLocaleDateString('es-MX', { dateStyle: 'long' }) : '—';

const ValidarConstanciaIMMPublico: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenParam = searchParams.get('token') || '';

  const [token, setToken] = useState(tokenParam);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [error, setError] = useState('');

  const validar = async (t: string) => {
    if (!t.trim()) {
      setError('Ingrese el token de validación o escanee el código QR.');
      return;
    }
    setLoading(true);
    setError('');
    setResultado(null);
    try {
      const { data } = await axiosInstance.get(`/imm/constancias/validar/${encodeURIComponent(t.trim())}`);
      setResultado(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'No se pudo validar la constancia');
    } finally {
      setLoading(false);
    }
  };

  // Autovalida cuando la URL trae ?token=
  useEffect(() => {
    if (tokenParam) {
      validar(tokenParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenParam]);

  const handleValidarManual = () => {
    setSearchParams(token.trim() ? { token: token.trim() } : {});
    validar(token);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Box bg="#5b182b" py={8} px={{ base: 4, md: 10 }} color="white" textAlign="center">
        <Flex justify="center" align="center" mb={2}>
          <Box bg="white" borderRadius="full" w={14} h={14} display="flex" alignItems="center" justifyContent="center" mr={4}>
            <Icon as={MdVerifiedUser} color="#5b182b" boxSize={8} />
          </Box>
          <Box textAlign="left">
            <Heading size="lg">Validación de Constancia</Heading>
            <Text fontSize="sm" opacity={0.85}>Instancia Municipal de la Mujer</Text>
          </Box>
        </Flex>
        <Text fontSize="sm" opacity={0.75} mt={2}>H. Ayuntamiento de Totolac · Tlaxcala</Text>
      </Box>

      <Box py={8} px={{ base: 4, md: 10 }} maxW="700px" mx="auto">
        <VStack spacing={6} align="stretch">
          <HStack>
            <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" color="gray.500"
              onClick={() => navigate('/portal/2026')}>
              Portal Ciudadano
            </Button>
          </HStack>

          <Card shadow="md">
            <CardHeader bg="gray.50" borderTopRadius="md">
              <Heading size="sm" color="#5b182b">Verificar autenticidad</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Escanee el código QR del documento o ingrese su token de validación.
              </Text>
            </CardHeader>
            <CardBody>
              <InputGroup mb={4}>
                <Input placeholder="Token de validación"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleValidarManual()}
                  fontFamily="mono" />
                <InputRightElement width="6rem" height="full">
                  <Button size="sm" colorScheme="red" onClick={handleValidarManual} isLoading={loading}>
                    Validar
                  </Button>
                </InputRightElement>
              </InputGroup>

              {loading && <Flex justify="center" py={4}><Spinner color="#5b182b" /></Flex>}

              {error && (
                <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>
              )}

              {resultado && (
                <VStack align="stretch" spacing={4}>
                  <Alert status={resultado.valido ? 'success' : 'error'} borderRadius="md">
                    <AlertIcon as={resultado.valido ? CheckCircleIcon : WarningIcon} />
                    <Box>
                      <Text fontWeight="bold" fontSize="md">
                        {resultado.valido ? '✔ DOCUMENTO VÁLIDO' : '✖ DOCUMENTO NO VÁLIDO'}
                      </Text>
                      <Text fontSize="sm">
                        {resultado.valido
                          ? 'Esta constancia fue emitida por la autoridad y se encuentra vigente.'
                          : 'No se encontró una constancia vigente con este token, o ha sido cancelada.'}
                      </Text>
                    </Box>
                  </Alert>

                  {resultado.valido && (
                    <Box bg="white" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <Box>
                          <Text fontSize="xs" color="gray.500">FOLIO</Text>
                          <Text fontFamily="mono" fontWeight="bold">{resultado.folio || '—'}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">TIPO DE DOCUMENTO</Text>
                          <Text fontSize="sm">{resultado.tipoDocumento || '—'}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">FECHA DE EMISIÓN</Text>
                          <Text fontSize="sm">{fmtDate(resultado.fechaEmision)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">DEPENDENCIA EMISORA</Text>
                          <Text fontSize="sm">{resultado.dependenciaEmisora || 'Instancia Municipal de la Mujer'}</Text>
                        </Box>
                      </SimpleGrid>
                      <Divider mt={3} mb={2} />
                      <Text fontSize="xs" color="gray.400" textAlign="center">
                        Por confidencialidad, esta validación no muestra datos personales de la titular.
                      </Text>
                    </Box>
                  )}
                </VStack>
              )}
            </CardBody>
          </Card>

          <Text fontSize="xs" color="gray.400" textAlign="center">
            Instancia Municipal de la Mujer · H. Ayuntamiento de Totolac · Tlaxcala
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default ValidarConstanciaIMMPublico;
